import React, { useEffect, useMemo, useState } from 'react'
import { ICellRendererParams, IRowNode } from 'ag-grid-community'
import {
  GanttBackgroundCanvas,
  useLinearDatePositions,
  useOverrunPosition,
} from './GanttBackground'
import { GanttSlider } from './GanttSlider'
import { SLIDER_TRACK_CENTER } from './ganttSliderStyles'
import { ProjectPlanRow } from '../../../../../../../../pages/ProjectPlan/projectPlanOptions'
import objects from '../../../../../../../../../utils/objects'
import { GanttParameterVO } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import { MilestoneDetail } from '../../../../../../../../../domain/entity/MilestoneEntity'
import { featureFlagService } from '../../../../../../../../../utils/feature-flag'
import { GanttBarVisualType } from '../ganttUtil'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { useRowContextRegistration } from './WbsItemLink/useRowContextRegistration'
import { GanttRowItem, ganttRowItemService } from '../GanttRowItem'

const isGanttV2 = featureFlagService.isEnabled('gantt-v2')

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; rowId: string }
>(({ children, rowId }, ref) => (
  <div
    id={`sevend-ag-cell-gantt-chart-${rowId}`}
    className="sevend-ag-cell-gantt-chart"
    ref={ref}
    style={{ pointerEvents: 'none' }}
  >
    {children}
  </div>
))

type SliderContainerProps = {
  children: React.ReactNode
  isV2: boolean
  rowHeight?: number
}

const SliderContainer = ({
  children,
  isV2,
  rowHeight,
}: SliderContainerProps) => {
  const v2Top = `calc(50% - ${SLIDER_TRACK_CENTER}px)`
  const style: React.CSSProperties = isV2
    ? {
        left: 0,
        width: '100%',
        position: 'absolute',
        top: v2Top,
      }
    : {
        left: 0,
        width: '100%',
        height: '3px',
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        top: (rowHeight || 32) / 2 - 4,
        margin: '-1px',
      }
  return <div style={style}>{children}</div>
}

// By default, the selected row of the AGGrid is the edit target.
// Set GanntEditTargetNodeSelectionProps if you want to customize the target row.
export type GanntEditTargetNodeSelectionProps = {
  getTargetNodes: (params: GanttCellRendererProps) => IRowNode[] | undefined
  rememberTargetNodes: (nodes: IRowNode[] | undefined) => void
  clearTargetNodes: () => void
}

export interface GanttCellRendererProps extends ICellRendererParams {
  displayTimeScale: CalendarDateVO[]
  timeScale: CalendarDateVO[]
  parameter: GanttParameterVO
  updateRows: (row: ProjectPlanRow[]) => void
  setParameter: (param: GanttParameterVO) => void
  editable?: boolean
  dataPath?: string
  customEditTargetNodeSelection?: GanntEditTargetNodeSelectionProps
  milestones?: MilestoneDetail[]
  isV2?: boolean
  /**
   * 行データから GanttRowItem を構築する。
   * 指定時はデータ取得経路が GanttRowItem に切り替わり、wbsItem ボディ
   * （`data.body.wbsItem`）前提の直読み・レガシー編集経路は使われない。
   */
  getGanttRowItem?: (data: unknown) => GanttRowItem | undefined
  /** GanttBarVisualType を決定するコールバック */
  getVisualType?: (
    rowItem: GanttRowItem,
    hasChildren: boolean
  ) => GanttBarVisualType
  /** 依存線を表示するかどうか（デフォルト: true） */
  showDependency?: boolean
}

const CellRenderer = (props: GanttCellRendererProps) => {
  const { context } = props
  const ganttParameter: GanttParameterVO = context.ganttParameter
  const displayOptions =
    ganttParameter?.displayOptions ?? GanttParameterVO.defaultDisplayOptions()
  const aggregateField: AggregateField =
    context.aggregateField ?? AggregateField.WBS_ITEM_WORKLOAD
  const aggregateTarget: AggregateTarget =
    context.aggregateTarget ?? WbsItemType.TASK

  const ganttParams = {
    ...props,
    parameter: ganttParameter,
    displayTimeScale: context.ganttDisplayTimeScale,
    timeScale: context.ganttTimeScale,
    milestones: context.milestones,
    isV2: isGanttV2,
  }

  // データ取得経路: getGanttRowItem 指定時は GanttRowItem に集約済み（プログラム管理）、
  // 未指定時は wbsItem ボディ直読み（ProjectPlanNew）
  const isRowItemSourced = !!props.getGanttRowItem
  const showDependency = props.showDependency ?? true
  const prefix = props.dataPath ? `${props.dataPath}.` : ''

  // getGanttRowItem 経路（プログラム管理）は行データが不変更新されるため、props.data を
  // キーにメモ化して参照を安定させる（React.memo の GanttBackgroundCanvas / GanttSlider が
  // 不要に再レンダリングされるのを防ぐ）。props.getGanttRowItem はモジュールレベルで安定。
  const { getGanttRowItem } = props
  const rowData = props.data
  const memoizedRowItem = useMemo(
    () => (getGanttRowItem ? getGanttRowItem(rowData) : undefined),
    [getGanttRowItem, rowData]
  )
  // TODO: ProjectPlanNew で getGanttRowItem を明示的に渡すようにし、このフォールバックを除去する
  // フォールバック経路（ProjectPlanNew）は従来どおり毎レンダー再構築し、挙動を変えない。
  const rowItem = props.getGanttRowItem
    ? memoizedRowItem
    : (() => {
        const wbsItem = objects.getValue(props.data, `${prefix}wbsItem`)
        if (!wbsItem) return undefined
        const cumulationData = objects.getValue(
          props.data,
          `${prefix}cumulation`
        )
        return {
          // uuid は AG Grid 行 uuid（= projectPlanUuid）、wbsItemUuid は WbsItem の UUID
          uuid: props.data?.uuid ?? wbsItem.uuid,
          wbsItemUuid: wbsItem.uuid,
          scheduledDate: wbsItem.scheduledDate,
          actualDate: wbsItem.actualDate,
          projectPlanCumulation: cumulationData,
          status: wbsItem.status,
          displayName: wbsItem.displayName,
          type: wbsItem.wbsItemType?.code ?? '',
          wbsItemType: wbsItem.wbsItemType,
          iconUrl: wbsItem.wbsItemType?.iconUrl,
          responsible: wbsItem.responsible,
          editable: props.editable !== false,
        } as GanttRowItem
      })()

  const datePositions = useLinearDatePositions(ganttParams.timeScale ?? [])
  // 文字列日付 → Date の生成を文字列値でメモ化し、毎レンダーの new Date() による
  // useOverrunPosition の不要な再計算を防ぐ。
  const scheduledEndDate = useMemo(
    () =>
      rowItem?.scheduledDate?.endDate
        ? new Date(rowItem.scheduledDate.endDate)
        : undefined,
    [rowItem?.scheduledDate?.endDate]
  )
  const overrunMaxScheduledEndDate = useMemo(
    () =>
      rowItem?.projectPlanCumulation?.maxScheduledEndDate
        ? new Date(rowItem.projectPlanCumulation.maxScheduledEndDate)
        : undefined,
    [rowItem?.projectPlanCumulation?.maxScheduledEndDate]
  )
  const overrunResult = useOverrunPosition(
    datePositions,
    scheduledEndDate,
    overrunMaxScheduledEndDate
  )
  // プログラム管理でも cumulation.maxScheduledEndDate があれば超過バーを表示する。
  // maxScheduledEndDate が無ければ overrunResult は空となり描画されない。
  const overrun = overrunResult

  // Track rowIndex as state so that changes (detected via modelUpdated)
  // trigger re-renders and propagate through React.memo children.
  // ag-grid mutates node.rowIndex in place without triggering React re-render.
  const [rowIndex, setRowIndex] = useState(props.node?.rowIndex ?? -1)

  useEffect(() => {
    const api = props.api
    const node = props.node
    if (!api || !node) return

    const onModelUpdated = () => {
      const idx = node.rowIndex
      if (idx !== undefined && idx !== null && idx >= 0) {
        setRowIndex(idx)
      }
    }

    api.addEventListener('modelUpdated', onModelUpdated)
    return () => api.removeEventListener('modelUpdated', onModelUpdated)
  }, [props.api, props.node])

  useRowContextRegistration({
    rowIndex,
    rowItem,
    projectPlanUuid: props.data?.uuid,
    showDependency,
  })

  if (!rowItem || !ganttParams.displayTimeScale || !ganttParams.timeScale) {
    return <></>
  }

  const hasChildren =
    (props.node.allLeafChildren && props.node.allLeafChildren.length > 0) ||
    false

  // TODO: ProjectPlanNew で getVisualType を明示的に渡すようにし、このフォールバックを除去する
  const visualType = props.getVisualType
    ? props.getVisualType(rowItem, hasChildren)
    : ganttRowItemService.getBarVisualType(rowItem, hasChildren)

  return (
    <>
      <ChartContainer rowId={props.node.id || ''}>
        <GanttBackgroundCanvas
          {...ganttParams}
          rowIndex={rowIndex}
          visualType={visualType}
          rowItem={rowItem}
        />
      </ChartContainer>
      <SliderContainer
        isV2={isGanttV2}
        rowHeight={props.node.rowHeight ?? undefined}
      >
        <GanttSlider
          {...ganttParams}
          // 行単位の編集可否は GanttRowItem.editable に集約する
          // （フォールバック構築時は従来どおり列の editable、ProgramManagement は行タイプで設定）
          editable={!!rowItem.editable}
          onScheduledDateEdited={context.onGanttBarDateEdited}
          onGanttBarClicked={context.onGanttBarClicked}
          displayOptions={displayOptions}
          aggregateField={aggregateField}
          aggregateTarget={aggregateTarget}
          cumulation={rowItem.projectPlanCumulation}
          overrunLeft={overrun?.left}
          overrunWidth={overrun?.width}
          hasChildren={hasChildren}
          visualType={visualType}
          rowItem={rowItem}
          rowItemSourced={isRowItemSourced}
        />
      </SliderContainer>
    </>
  )
}

export default CellRenderer
